import { Sparkles } from "lucide-react";
import { useCms } from "../../cms/CmsContext";

/**
 * EmiTickerBanner — slim running-text strip placed below the header.
 * Content (messages, speed, direction, separator, on/off) is managed from
 * Admin → Website Content → Global Content → Announcement Banner. Falls
 * back to the original single EMI message if CMS content is unavailable or
 * empty, so the banner never disappears unexpectedly.
 */
const DEFAULT_MESSAGES = [
  { id: "default", text: "No-cost EMI is available for all customers, subject to eligibility only", enabled: true },
];

export default function EmiTickerBanner() {
  const { data } = useCms();
  const banner = data?.announcement_banner || {};

  if (banner.enabled === false) return null;

  const messages = Array.isArray(banner.messages) && banner.messages.length > 0 ? banner.messages : DEFAULT_MESSAGES;
  const activeMessages = messages.filter((m) => m.enabled !== false);
  if (activeMessages.length === 0) return null;

  const speed = Number(banner.speed_seconds) > 0 ? Number(banner.speed_seconds) : 45;
  const direction = banner.direction === "right" ? "right" : "left";
  const separator = banner.separator || "·";

  // Repeat the message set enough times to fill ultrawide screens with
  // overlap, so the -50% translate loop never reveals empty space.
  const repeatCount = Math.max(2, Math.ceil(8 / activeMessages.length));
  const items = Array.from({ length: repeatCount }).flatMap(() => activeMessages);
  const ariaLabel = activeMessages.map((m) => m.text).join(" — ");

  return (
    <div
      data-testid="emi-ticker-banner"
      role="status"
      aria-label={ariaLabel}
      className="fixed top-16 lg:top-20 inset-x-0 z-[55] w-full overflow-hidden border-y border-gold/25 bg-gradient-to-r from-[var(--gx-black)] via-[#1a1409] to-[var(--gx-black)]"
    >
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[var(--gx-black)] to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[var(--gx-black)] to-transparent z-10" />

      <div className="emi-ticker-viewport flex justify-center overflow-hidden">
        <div
          className="emi-ticker-track flex items-center gap-12 py-2.5 whitespace-nowrap"
          style={{ animationDuration: `${speed}s`, animationDirection: direction === "right" ? "reverse" : "normal" }}
        >
          {items.map((m, i) => (
            <span
              key={`${m.id || m.text}-${i}`}
              data-testid={`emi-ticker-message-${i}`}
              className="inline-flex items-center gap-3 text-[12px] sm:text-[12.5px] tracking-[0.18em] uppercase text-amber-200/90"
            >
              <Sparkles className="w-3.5 h-3.5 text-gold shrink-0" />
              <span>{m.text}</span>
              <span className="text-gold/40">{separator}</span>
            </span>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes emi-ticker-scroll {
          from { transform: translate3d(0, 0, 0); }
          to   { transform: translate3d(-50%, 0, 0); }
        }
        .emi-ticker-track {
          width: max-content;
          animation-name: emi-ticker-scroll;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          will-change: transform;
        }
        .emi-ticker-track:hover {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .emi-ticker-track { animation: none; }
        }
      `}</style>
    </div>
  );
}
